import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiCall } from 'services';
import type { AuthFactorType } from '@repo/types';

/**
 * VRG-490 — client for `AuthCustomerController`'s `2fa/*` routes
 * (`apps/api/src/infrastructure/controllers/auth.customer.controller.ts`).
 * Mirrors the response DTOs 1:1, the same pattern `apps/platform`'s sibling
 * hook (`hooks/api/useAuthFactors.ts`) uses.
 */

export interface AuthFactor {
  id: string;
  type: AuthFactorType;
  label: string | null;
  phoneLastFour: string | null;
  verified: boolean;
  lastUsedAt: string | null;
  /** SMS only: set while texts to this number are blocked because it replied STOP. */
  optedOutAt?: string | null;
  createdAt: string;
}

export interface StartTotpEnrollmentResponse {
  factorId: string;
  secret: string;
  otpauthUri: string;
  qrCodeDataUri: string;
}

export interface StartSmsEnrollmentResponse {
  reference: string;
}

export interface ConfirmAuthFactorResponse {
  factor: {
    id: string;
    type: AuthFactorType;
    label: string | null;
    verified_at: string;
  };
  recoveryCodes?: string[];
}

type AuthFactorApiError = Error & { status?: number };

const toMutationError = (status: number, fallbackMessage: string, message?: string): AuthFactorApiError => {
  const error = new Error(message || fallbackMessage) as AuthFactorApiError;
  error.status = status;
  return error;
};

/**
 * VRG-490 — a rejected DTO (e.g. an SMS-enrollment phone the global
 * `ValidationPipe` couldn't parse) responds with `message: "Validation
 * errors"` — a fixed, generic string — and puts the actual per-field reason
 * in `details` (`{[field]: string[]}`). Reading `message` alone surfaced the
 * literal words "Validation errors" to the user. Mirrors the identical fix
 * in the platform sibling hook (`apps/platform/src/hooks/api/useAuthFactors.ts`)
 * — same API contract, same bug, same fix.
 */
const extractApiErrorMessage = (data: unknown, fallback: string): string => {
  const details = (data as { details?: Record<string, unknown> } | undefined)?.details;
  const fieldMessages = Object.values(details ?? {})
    .flat()
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  if (fieldMessages.length > 0) return fieldMessages[0];

  const message = (data as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string' && message !== 'Validation errors') return message;
  if (Array.isArray(message) && message.length > 0 && typeof message[0] === 'string') return message[0];

  return fallback;
};

const AUTH_FACTORS_QUERY_KEY = ['auth', 'factors'] as const;

export const useAuthFactorsQuery = () => {
  const query = useQuery<AuthFactor[]>({
    queryKey: AUTH_FACTORS_QUERY_KEY,
    queryFn: async () => {
      const response = await createApiCall({ method: 'GET', url: '/auth/customer/2fa/factors' });
      if (response.status !== 200) {
        throw toMutationError(response.status, 'Failed to load your two-factor methods');
      }
      return (response.data?.factors ?? []) as AuthFactor[];
    },
  });

  return {
    factors: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};

export const useStartTotpEnrollmentMutation = () => {
  const mutation = useMutation<StartTotpEnrollmentResponse, AuthFactorApiError, void>({
    mutationFn: async () => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/totp/enroll' });
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          'Failed to start authenticator app enrollment',
          extractApiErrorMessage(response.data, 'Failed to start authenticator app enrollment'),
        );
      }
      return response.data as StartTotpEnrollmentResponse;
    },
  });
  return {
    startTotpEnrollmentAsync: mutation.mutateAsync,
    isStartingTotpEnrollment: mutation.isPending,
  };
};

export const useConfirmTotpEnrollmentMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation<ConfirmAuthFactorResponse, AuthFactorApiError, { code: string }>({
    mutationFn: async payload => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/totp/confirm', data: payload });
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          "That code isn't right. Try again.",
          extractApiErrorMessage(response.data, "That code isn't right. Try again."),
        );
      }
      return response.data as ConfirmAuthFactorResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY });
    },
  });
  return {
    confirmTotpEnrollmentAsync: mutation.mutateAsync,
    isConfirmingTotpEnrollment: mutation.isPending,
  };
};

export const useStartSmsEnrollmentMutation = () => {
  const mutation = useMutation<StartSmsEnrollmentResponse, AuthFactorApiError, { phone: string }>({
    mutationFn: async payload => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/sms/enroll', data: payload });
      if (response.status !== 201) {
        if (response.status === 429) {
          throw toMutationError(response.status, "You've requested too many codes. Please wait 15 minutes and try again.");
        }
        throw toMutationError(
          response.status,
          "We couldn't send a code to that number. Check it and try again.",
          extractApiErrorMessage(response.data, "We couldn't send a code to that number. Check it and try again."),
        );
      }
      return response.data as StartSmsEnrollmentResponse;
    },
  });
  return {
    startSmsEnrollmentAsync: mutation.mutateAsync,
    isStartingSmsEnrollment: mutation.isPending,
  };
};

export const useConfirmSmsEnrollmentMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation<ConfirmAuthFactorResponse, AuthFactorApiError, { reference: string; code: string }>({
    mutationFn: async payload => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/sms/confirm', data: payload });
      if (response.status !== 201) {
        if (response.status === 404) {
          throw toMutationError(response.status, 'That code has expired. Request a new one.');
        }
        throw toMutationError(
          response.status,
          "That code isn't right. Try again.",
          extractApiErrorMessage(response.data, "That code isn't right. Try again."),
        );
      }
      return response.data as ConfirmAuthFactorResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY });
    },
  });
  return {
    confirmSmsEnrollmentAsync: mutation.mutateAsync,
    isConfirmingSmsEnrollment: mutation.isPending,
  };
};

export const useRemoveAuthFactorMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation<void, AuthFactorApiError, { factorId: string }>({
    mutationFn: async ({ factorId }) => {
      const response = await createApiCall({ method: 'DELETE', url: `/auth/customer/2fa/factors/${factorId}` });
      if (response.status !== 200) {
        throw toMutationError(
          response.status,
          'Failed to remove that method',
          extractApiErrorMessage(response.data, 'Failed to remove that method'),
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY });
    },
  });
  return {
    removeAuthFactorAsync: mutation.mutateAsync,
    isRemovingAuthFactor: mutation.isPending,
  };
};

export const useRegenerateRecoveryCodesMutation = () => {
  const mutation = useMutation<{ recoveryCodes: string[] }, AuthFactorApiError, void>({
    mutationFn: async () => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/recovery-codes/regenerate' });
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          'Failed to generate new recovery codes',
          extractApiErrorMessage(response.data, 'Failed to generate new recovery codes'),
        );
      }
      return response.data as { recoveryCodes: string[] };
    },
  });
  return {
    regenerateRecoveryCodesAsync: mutation.mutateAsync,
    isRegeneratingRecoveryCodes: mutation.isPending,
  };
};

/**
 * Step-up before changing second factors (security review 2026-10-09). Any add, remove or
 * recovery-code regeneration while a factor exists first needs the owner to re-prove that
 * factor; the API refuses with STEP_UP_REQUIRED otherwise. Without it, a stolen session token
 * alone could remove the owner's factors and enroll its own.
 */
export interface StepUpChallenge {
  ticket: string;
  factorType: AuthFactorType;
  maskedPhone?: string;
}

export const useStartStepUpMutation = () => {
  const mutation = useMutation<StepUpChallenge, AuthFactorApiError, void>({
    mutationFn: async () => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/step-up/start' });
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          'Could not start verification',
          extractApiErrorMessage(response.data, 'Could not start verification'),
        );
      }
      return response.data as StepUpChallenge;
    },
  });
  return { startStepUpAsync: mutation.mutateAsync, isStartingStepUp: mutation.isPending };
};

export const useVerifyStepUpMutation = () => {
  const mutation = useMutation<void, AuthFactorApiError, { ticket: string; code: string }>({
    mutationFn: async (payload) => {
      const response = await createApiCall({ method: 'POST', url: '/auth/customer/2fa/step-up/verify', data: payload });
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          "That code isn't right. Try again.",
          extractApiErrorMessage(response.data, "That code isn't right. Try again."),
        );
      }
    },
  });
  return { verifyStepUpAsync: mutation.mutateAsync, isVerifyingStepUp: mutation.isPending };
};
